/**
 * ResidentEpisodePanel — active Resident Trends concerns on the resident
 * profile: the resident's open episode(s) with lifecycle actions, plus any
 * active expected-behavior mutes.
 *
 * Closed episodes are NOT shown here — they live on the Trend History tab so a
 * growing history cannot crowd out the rest of the Basic Info tab.
 *
 * Presentational: the parent owns the fetch (one call serves both this panel
 * and the history tab) and passes `onChanged` to trigger a reload after a
 * lifecycle write.
 *
 * Props:
 *   campusId  — current campus
 *   episodes  — OPEN episodes only (new / acknowledged / in_progress)
 *   mutes     — active mute rows for this resident
 *   onChanged — called after a successful write so the parent can refetch
 */

import { useState, useCallback } from 'react';
import { getUserId, getSessionId } from '../../services/session.js';
import { updateResidentEpisode, clearTrendMute } from './episodesApi.js';
import EpisodeCard from './EpisodeCard.jsx';
import { FAMILY_LABELS } from './trendConfig.js';

const formatDate = (value) => (value ? String(value).slice(0, 10) : '');

const ResidentEpisodePanel = ({ campusId, episodes, mutes, onChanged }) => {
  const [processingIds, setProcessingIds] = useState(new Set());
  const [actionError, setActionError] = useState(null);

  const openEpisodes = episodes || [];
  const activeMutes = mutes || [];

  const applyUpdate = useCallback(async (episodeId, status, extra = {}) => {
    setProcessingIds((prev) => new Set(prev).add(episodeId));
    setActionError(null);
    const [ok, errMsg] = await updateResidentEpisode({
      campusId, episodeId, status, ...extra,
      userId: getUserId(), clientId: getSessionId(),
    });
    if (ok) {
      if (onChanged) await onChanged();
    } else {
      setActionError(errMsg || 'Update failed. Please try again.');
    }
    setProcessingIds((prev) => { const n = new Set(prev); n.delete(episodeId); return n; });
  }, [campusId, onChanged]);

  const handleAcknowledge = useCallback((id) => applyUpdate(id, 'acknowledged'), [applyUpdate]);
  const handleResolve = useCallback((id, actionTaken) => applyUpdate(id, 'resolved', { actionTaken }), [applyUpdate]);
  const handleDismiss = useCallback((id, reason, muteDays) => applyUpdate(id, 'dismissed', { dismissReason: reason, muteDays }), [applyUpdate]);

  const handleUnmute = useCallback(async (threadId) => {
    setActionError(null);
    const [ok, errMsg] = await clearTrendMute({
      campusId, threadId, userId: getUserId(), clientId: getSessionId(),
    });
    if (ok) {
      if (onChanged) await onChanged();
    } else {
      setActionError(errMsg || 'Update failed. Please try again.');
    }
  }, [campusId, onChanged]);

  if (openEpisodes.length === 0 && activeMutes.length === 0) return null;

  return (
    <div style={{ marginBottom: 'var(--space-3)' }}>
      {actionError ? (
        <div style={{ margin: '0 0 8px', padding: '6px 12px', borderRadius: 'var(--radius-md)', background: 'rgba(231,76,60,0.08)', color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>
          {actionError}
        </div>
      ) : null}

      {openEpisodes.map((episode) => (
        <EpisodeCard
          key={episode.episode_id}
          episode={episode}
          showResident={false}
          onAcknowledge={handleAcknowledge}
          onResolve={handleResolve}
          onDismiss={handleDismiss}
          isProcessing={processingIds.has(episode.episode_id)}
        />
      ))}

      {activeMutes.length > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', margin: '4px 0 0' }}>
          {activeMutes.map((mute) => (
            <span key={mute.thread_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-xs)', padding: '2px 10px', borderRadius: 'var(--radius-full)', background: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
              🔕 {FAMILY_LABELS[mute.signal_family] || mute.signal_family} trends muted until {formatDate(mute.muted_until)}
              <span onClick={() => handleUnmute(mute.thread_id)} style={{ cursor: 'pointer', fontWeight: 600, color: 'var(--color-text-body)' }} title="Clear this mute">✕</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default ResidentEpisodePanel;
